import { inject, Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import type { ID } from '@core/ngrx-legacy';
import { TellentHttpApiService } from '@core/tellent-http-legacy';
import {
  AddEmployeeForPreonboardingPayload,
  AddEmployeeWithoutAccessPayload,
  APP_GUID,
  BulkDeactivateResponse,
  BulkReactivateResponse,
  BulkResendInvitationResponse,
  BulkResetPasswordResponse,
  BulkUpdateMembersPayload,
  InviteMemberPayload,
  ManageSeatsResponse,
  MemberDataUpdate,
  TeamMembersPaginationParams,
  TellentMember,
  TellentMemberResponse,
  TellentMembersResponse,
  TellentOrgAbilities,
  TellentOrgAbilitiesExtended,
} from '@core/tellent-types';

import type { ManageSeatsPayload } from '../types';

@Injectable({ providedIn: 'root' })
export class TellentMembersHttpClient {
  protected readonly httpApi = inject(TellentHttpApiService);

  private appGuid = inject(APP_GUID);

  public inviteTellentMember(
    payload:
      | InviteMemberPayload
      | AddEmployeeForPreonboardingPayload
      | AddEmployeeWithoutAccessPayload,
  ): Observable<TellentMember[]> {
    return this.httpApi
      .post('/members/add', null, payload)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public inviteExistingMember(data: BulkUpdateMembersPayload): Observable<TellentMember[]> {
    return this.httpApi
      .patch(`/members/update`, null, data)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public inviteAppMember(
    payload:
      | InviteMemberPayload
      | AddEmployeeForPreonboardingPayload
      | AddEmployeeWithoutAccessPayload,
  ): Observable<TellentMember[]> {
    return this.httpApi
      .post(`/api/app/${this.appGuid}/members/add`, null, payload, { skipCompany: true })
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchMembersByGuid(guids: string[]): Observable<TellentMember[]> {
    return this.httpApi
      .get('/members', { guids }, null)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchMemberByEmail(email: string): Observable<TellentMember[]> {
    return this.httpApi
      .get('/members', { email }, null)
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchAppMemberByEmail(email: string): Observable<TellentMember[]> {
    return this.httpApi
      .get(`/api/app/${this.appGuid}/members`, { email }, { skipCompany: true })
      .pipe(map((response: TellentMembersResponse) => response.data));
  }

  public fetchAppMembers(params: TeamMembersPaginationParams): Observable<TellentMembersResponse> {
    const { page, limit, q, statuses, sortBy, sortOrder, guids } = params;
    return this.httpApi.get(
      `/api/app/${this.appGuid}/members`,
      {
        page,
        limit,
        q,
        statuses,
        sort_by: sortBy,
        sort_dir: sortOrder,
        ...(guids ? { guids } : {}),
      },
      { skipCompany: true },
    );
  }

  public fetchMemberByGuid(guid: ID | string): Observable<TellentMemberResponse> {
    return this.httpApi.get(`/members/${guid}`);
  }

  public fetchMembers(params: TeamMembersPaginationParams): Observable<TellentMembersResponse> {
    const { page, limit, q, statuses, sortBy, sortOrder, guids } = params;
    return this.httpApi.get('/members', {
      page,
      limit,
      q,
      statuses,
      sort_by: sortBy,
      sort_dir: sortOrder,
      ...(guids ? { guids } : {}),
    });
  }

  public deactivateMember(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${payload.guid}/deactivate`);
  }

  public bulkDeactivateMembers(payload: { guids: string[] }): Observable<BulkDeactivateResponse> {
    return this.httpApi.patch('/members/deactivate', null, { guids: payload.guids });
  }

  public bulkReactivateMembers(payload: { guids: string[] }): Observable<BulkReactivateResponse> {
    return this.httpApi.patch('/members/reactivate', null, { guids: payload.guids });
  }

  public bulkResetPasswords(payload: { guids: string[] }): Observable<BulkResetPasswordResponse> {
    return this.httpApi.post(`/members/reset-passwords`, null, { guids: payload.guids });
  }

  public bulkResendInvitations(payload: {
    guids: string[];
  }): Observable<BulkResendInvitationResponse> {
    return this.httpApi.post(`/members/resend-invitations`, null, { guids: payload.guids });
  }

  public deleteMember(payload: { guid: string }): Observable<{ success: true }> {
    return this.httpApi.delete(`/members/${payload.guid}/remove`);
  }

  public reactivateMember(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${payload.guid}/reactivate`);
  }

  public sendInvitation(payload: { guid: string }): Observable<TellentMemberResponse> {
    return this.httpApi.post(`/members/${payload.guid}/invite`);
  }
  public manageSeats(payload: ManageSeatsPayload): Observable<ManageSeatsResponse> {
    return this.httpApi.post(`/seats/manage`, null, payload);
  }

  public updateMemberData(payload: MemberDataUpdate): Observable<TellentMemberResponse> {
    const { guid, ...rest } = payload;
    const formData = new FormData();

    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined) return;
      if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, value === null ? '' : `${value}`);
      }
    });

    return this.httpApi.patch(`/members/${guid}/update`, null, formData);
  }

  public sendPasswordResetInstructions(userGuid: string): Observable<void> {
    return this.httpApi.post(`/users/${userGuid}/reset-password`);
  }

  public updateAbilities(
    userGuid: string,
    abilities: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>,
  ): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${userGuid}/update-abilities`, null, {
      abilities: [...abilities],
    });
  }

  public addGroups(payload: {
    memberGuid: string;
    groupsGuids: string[];
  }): Observable<TellentMemberResponse> {
    return this.manageGroups(payload.memberGuid, payload.groupsGuids, 'assign');
  }

  public removeGroups(payload: {
    memberGuid: string;
    groupsGuids: string[];
  }): Observable<TellentMemberResponse> {
    return this.manageGroups(payload.memberGuid, payload.groupsGuids, 'unassign');
  }

  public generateTellentPasswords(payload: { membersGuids: string[] }): Observable<Blob> {
    return this.httpApi.post(
      `/members/download-passwords`,
      null,
      {
        membersGuids: payload.membersGuids,
      },
      { responseType: 'blob' },
    );
  }

  public generateAppPasswords(payload: { membersGuids: string[] }): Observable<Blob> {
    return this.httpApi.post(
      `/api/app/${this.appGuid}/members/download-passwords`,
      null,
      {
        membersGuids: payload.membersGuids,
      },
      {
        responseType: 'blob',
        skipCompany: true,
      },
    );
  }

  public changeLoginMethod(payload: { guid: string; email: string }) {
    return this.httpApi.patch(`/members/${payload.guid}/change-login-method`, null, {
      email: payload.email,
    });
  }

  private manageGroups(
    memberGuid: string,
    groupsGuids: string[],
    action: 'assign' | 'unassign',
  ): Observable<TellentMemberResponse> {
    return this.httpApi.patch(`/members/${memberGuid}/${action}`, null, {
      teamsGuids: groupsGuids,
    });
  }
}
